import { getTimeframeSeconds } from '../../utils/chartUtils'

/**
 * The walkthrough transport's pacing: ONE number, the wall-clock milliseconds a bar dwells.
 *
 * The Backtest tab's bar picks a literal engine speed (0.5x … 50x) from a bracketed table. A
 * walkthrough is watched rather than scanned, and its transport used to speak in two terms — a
 * DETAIL (candle | tick) and a PACE whose meaning changed with the detail. That is gone: there is
 * one slider, Real-time on the left and Fast on the right, and the value it sets is a fixed
 * wall-clock time per bar that is INDEPENDENT OF THE TIMEFRAME. An M1 bar and an H1 bar take the
 * same time to arrive; the ticks inside are paced to fit that time.
 *
 * The knob's left end is REAL TIME: a bar dwells its whole timeframe and every tick lands at its
 * own time (the engine's market-clock regime, see `isMarketClockDwell` in formingBarPlan). It is
 * one stop, REAL_TIME_BAR_MS, not a stretch of the track: that geometry's step count follows the
 * dwell and would rebuild the sub-bar ring on every nudge of the knob. The rest of the track,
 * from SLOWEST_BAR_MS to FASTEST_BAR_MS, never reaches it.
 *
 * Everything the slider can produce is a constant here, so the feel is tuned in one place.
 */

/** The slider's left end: a bar takes its own timeframe of wall clock. */
export const REAL_TIME_BAR_MS = 0
/** The slowest pace short of real time: one bar every 1.5s. */
export const SLOWEST_BAR_MS = 1500
/**
 * The slider's RIGHT end, "Fast". Tuned by watching, so the number here is the feel that read
 * right, not a derivation. However short it goes the play loop still reveals ONE bar per
 * interval (the override pins its batch at one), so a faster pace is more reveals, never
 * several bars in one redraw; the forming bar has long stood down by then (FORMING_BAR_MIN_MS).
 */
export const FASTEST_BAR_MS = 50
/** Where every run opens. On the slow half, because a walkthrough exists to be watched. */
export const DEFAULT_BAR_MS = 1000
/**
 * Below this the bar pops in whole instead of forming from ticks.
 *
 * The forming bar paints at most once per ~33ms frame, so a 400ms bar shows about twelve
 * distinct states — around the fewest that still read as a candle BUILDING rather than as a
 * flicker. Under it the animation would cost its full sub-bar fetch (up to ~110 KB per M5 bar
 * in the busiest sessions) for a build nobody can see, so it stands down and the whole-bar
 * reveal takes over. One number to move if the feel wants it elsewhere.
 */
export const FORMING_BAR_MIN_MS = 400
/** Slider resolution: 200 positions across the track, fine enough to read as continuous. */
export const BAR_SPEED_STEP = 0.005
/**
 * The engine speed the walkthrough reports. Nominal: every timing decision comes from the
 * wall-clock override, which `usePlayback` and `useFormingBar` both consult before the speed
 * table. A member of AVAILABLE_SPEEDS so nothing downstream meets a number it has no bracket for.
 */
export const WALKTHROUGH_ENGINE_SPEED = 2

// THE MAPPING IS LOGARITHMIC because speed is perceived as a ratio, not a difference: 1500→1300ms
// is barely noticeable while 350→250ms is obvious, so an even linear track would spend most of
// its length in the range nobody can tell apart. In log space every millimetre of track is the
// same proportional change, which is what makes the drag feel even.
const LN_SLOWEST = Math.log(SLOWEST_BAR_MS)
const LN_FASTEST = Math.log(FASTEST_BAR_MS)

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0
}

export function isRealTime(barMs: number): boolean {
  return barMs === REAL_TIME_BAR_MS
}

/** A dwell the slider can actually reach. Anything unreadable becomes the default rather than NaN. */
export function clampBarMs(ms: number): number {
  if (isRealTime(ms)) return REAL_TIME_BAR_MS
  if (!Number.isFinite(ms)) return DEFAULT_BAR_MS
  return Math.round(Math.min(SLOWEST_BAR_MS, Math.max(FASTEST_BAR_MS, ms)))
}

/** Slider position (0 = Real-time … 1 = Fast) → milliseconds per bar; the paced range starts one step in. */
export function msForPosition(position: number): number {
  const p = clamp01(position)
  if (p < BAR_SPEED_STEP / 2) return REAL_TIME_BAR_MS
  const t = clamp01((p - BAR_SPEED_STEP) / (1 - BAR_SPEED_STEP))
  return Math.round(Math.exp(LN_SLOWEST + t * (LN_FASTEST - LN_SLOWEST)))
}

/** Milliseconds per bar → slider position. Derived, never stored: the ms is the one source of truth. */
export function positionForMs(ms: number): number {
  const clamped = clampBarMs(ms)
  if (isRealTime(clamped)) return 0
  const t = clamp01((Math.log(clamped) - LN_SLOWEST) / (LN_FASTEST - LN_SLOWEST))
  return BAR_SPEED_STEP + t * (1 - BAR_SPEED_STEP)
}

/** The wall-clock milliseconds a bar of this timeframe dwells at this pace. */
export function dwellMs(barMs: number, timeframe: string): number {
  return isRealTime(barMs) ? getTimeframeSeconds(timeframe) * 1000 : barMs
}

/** Whether a bar at this dwell is drawn as it forms, or pops in whole. */
export function buildsFormingBar(dwell: number): boolean {
  return dwell >= FORMING_BAR_MIN_MS
}

/** The readout shown while the knob is held: "1.0s / bar". */
export function barSpeedLabel(barMs: number): string {
  return isRealTime(barMs) ? 'Real-time' : `${(barMs / 1000).toFixed(1)}s / bar`
}
